import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import walletService from '../services/walletService';
import '../styles/wallet.css';

const Compras = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('transactions');

  // Función para cargar datos de compras y transacciones
  const fetchPurchaseData = async () => {
    setLoading(true);
    try {
      // Obtener transacciones detalladas del usuario
      const response = await walletService.getDetailedTransactions();

      if (response.success && response.data) {
        // Transformar las transacciones para que coincidan con el formato esperado
        const transformedTransactions = response.data.transactions.map(transaction => {
          console.log('🔍 DEBUG - Transacción recibida:', transaction);
          if (transaction.inscripcionData) {
            console.log('📋 DEBUG - Datos de inscripción:', transaction.inscripcionData);
          }

          return {
            id: transaction._id,
            type: transaction.type,
            amount: transaction.amount,
            description: transaction.description,
            date: new Date(transaction.createdAt),
            status: transaction.status,
            // Datos adicionales para mostrar información específica
            inscripcionData: transaction.inscripcionData,
            paymentMethod: transaction.paymentMethod,
            transactionId: transaction.transactionId
          };
        });

        setTransactions(transformedTransactions);
      } else {
        console.error('❌ Error en la respuesta del servicio:', response);
        setError('No se pudieron cargar las transacciones');
        setTransactions([]);
      }
    } catch (error) {
      console.error('❌ Error al cargar datos de compras:', error);
      setError('Error al cargar las transacciones');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPurchaseData();
    }
  }, [user]);

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'match_payment':
        return 'sports_soccer';
      case 'product_purchase':
        return 'shopping_bag';
      case 'wallet_deposit':
        return 'add_circle';
      case 'wallet_withdrawal':
        return 'remove_circle';
      case 'reward':
        return 'emoji_events';
      case 'refund':
        return 'replay';
      // Compatibilidad con tipos antiguos
      case 'deposit':
        return 'add_circle';
      case 'payment':
        return 'shopping_cart';
      default:
        return 'receipt_long';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'wallet_deposit':
      case 'reward':
      case 'refund':
        return 'positive';
      case 'wallet_withdrawal':
      case 'match_payment':
      case 'product_purchase':
        return 'negative';
      // Compatibilidad con tipos antiguos
      case 'deposit':
        return 'positive';
      case 'payment':
        return 'negative';
      default:
        return 'neutral';
    }
  };

  const getTransactionDescription = (transaction) => {
    if (transaction.inscripcionData) {
      const { futbolista_nombre, equipacion } = transaction.inscripcionData;
      let desc = `Inscripción de ${futbolista_nombre}`;
      if (equipacion?.incluir) {
        desc += ` (incluye equipación talla ${equipacion.talla})`;
      }
      return desc;
    }
    return transaction.description || 'Transacción';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { text: 'Completado', class: 'success' },
      pending: { text: 'Pendiente', class: 'warning' },
      failed: { text: 'Fallido', class: 'error' },
      cancelled: { text: 'Cancelado', class: 'neutral' },
      processing: { text: 'Procesando', class: 'info' }
    };

    return statusConfig[status] || { text: status, class: 'neutral' };
  };

  if (loading) {
    return <div className="loading">Cargando historial de compras...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="wallet-page">
      <div className="container">
        <div className="page-header">
          <h1>Mis Compras</h1>
          <p>Revisa tu historial de compras y transacciones</p>
          <div className="back-link">
            <Link to="/profile" className="btn btn-secondary">
              <span className="material-icons">arrow_back</span> Volver al Perfil
            </Link>
          </div>
        </div>

        <div className="wallet-content">
          <div className="wallet-main">

            {/* Botón para comprar jornadas - arriba del historial */}
            <div className="wallet-action-button" style={{ marginBottom: '20px' }}>
              <Link to="/products" className="btn btn-primary" style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 20px',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                <span className="material-icons">shopping_cart</span>
                Comprar Jornadas y Productos
              </Link>
            </div>

            <div className="wallet-tabs">
              <div className="wallet-tab-header">
                <button
                  className={`wallet-tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('transactions')}
                >
                  <span className="material-icons">receipt_long</span>
                  Historial de Compras y Transacciones
                </button>
              </div>

              <div className="wallet-tab-content">
                {/* Pestaña de Transacciones */}
                <div
                  id="transactions-tab"
                  className={`wallet-tab-pane ${activeTab === 'transactions' ? 'active' : ''}`}
                >
                  {transactions.length === 0 ? (
                    <div className="empty-state">
                      <span className="material-icons">shopping_bag</span>
                      <h3>No hay compras realizadas</h3>
                      <p>Cuando realices compras o inscripciones, aparecerán aquí.</p>
                      <Link to="/products" className="btn btn-primary">
                        <span className="material-icons">shopping_cart</span>
                        Ir a la Tienda
                      </Link>
                    </div>
                  ) : (
                    <div className="transaction-list">
                      {transactions.map((transaction) => (
                        <div className="transaction-item" key={transaction.id || transaction._id}>
                          <div className={`transaction-icon ${getTransactionColor(transaction.type)}`}>
                            <span className="material-icons">
                              {getTransactionIcon(transaction.type)}
                            </span>
                          </div>
                          <div className="transaction-info">
                            <div className="transaction-title">
                              {getTransactionDescription(transaction)}
                            </div>
                            <div className="transaction-details">
                              {transaction.inscripcionData && (
                                <div className="purchase-details">
                                  <div className="detail-item">
                                    <span className="detail-label">Futbolista:</span>
                                    <span className="detail-value">{transaction.inscripcionData.futbolista_nombre}</span>
                                  </div>
                                  {transaction.inscripcionData.equipacion?.incluir && (
                                    <div className="detail-item">
                                      <span className="detail-label">Equipación:</span>
                                      <span className="detail-value">Talla {transaction.inscripcionData.equipacion.talla} - €{transaction.inscripcionData.equipacion.precio}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="transaction-meta">
                                <span className="transaction-date">
                                  {new Date(transaction.createdAt || transaction.date).toLocaleDateString('es-ES', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                                <span className="transaction-id">ID: {transaction.transactionId || transaction.id}</span>
                              </div>
                            </div>
                          </div>
                          <div className="transaction-amount-status">
                            <div className={`transaction-amount ${getTransactionColor(transaction.type)}`}>
                              €{transaction.amount.toFixed(2)}
                            </div>
                            <div className="transaction-status">
                              <span className={`status-badge ${getStatusBadge(transaction.status).class}`}>
                                {getStatusBadge(transaction.status).text}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="wallet-sidebar">
            <div className="wallet-info-card">
              <h2>Información de Compras</h2>
              <div className="info-content">
                <p>
                  Aquí puedes revisar todas tus compras, inscripciones y transacciones realizadas en Liga Prokids.
                </p>

                <h3>Tipos de transacciones:</h3>
                <ul>
                  <li>
                    <span className="material-icons">sports_soccer</span>
                    <span>Inscripciones a jornadas</span>
                  </li>
                  <li>
                    <span className="material-icons">shopping_bag</span>
                    <span>Compras de productos</span>
                  </li>
                  <li>
                    <span className="material-icons">checkroom</span>
                    <span>Equipación JOMA</span>
                  </li>
                  <li>
                    <span className="material-icons">emoji_events</span>
                    <span>Recompensas por logros</span>
                  </li>
                  <li>
                    <span className="material-icons">replay</span>
                    <span>Reembolsos automáticos</span>
                  </li>
                </ul>

                <h3>Estados de transacciones:</h3>
                <div className="status-legend">
                  <div className="status-item">
                    <span className="status-badge success">Completado</span>
                    <span>Pago procesado correctamente</span>
                  </div>
                  <div className="status-item">
                    <span className="status-badge warning">Pendiente</span>
                    <span>Esperando confirmación</span>
                  </div>
                  <div className="status-item">
                    <span className="status-badge error">Fallido</span>
                    <span>Error en el procesamiento</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="wallet-help-card">
              <h2>¿Necesitas ayuda?</h2>
              <p>
                Si tienes alguna pregunta sobre tus compras o transacciones, no dudes en
                contactarnos.
              </p>
              <Link to="/contact" className="btn btn-secondary">
                <span className="material-icons">support_agent</span> Contactar Soporte
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Compras;
